import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import { caseStudiesData } from '../data/caseStudies';

const { FiSlash, FiRefreshCw, FiMap, FiUserMinus } = FiIcons;

const Problem = () => {
  const { t, lang } = useLanguage();
  const icons = [FiSlash, FiRefreshCw, FiMap, FiUserMinus];
  const featuredStudies = caseStudiesData.slice(0, 2);
  const isSpanish = lang === 'es';

  return (
    <section className="py-24 px-6 bg-zinc-900">
      <div className="max-w-6xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl md:text-5xl font-bold text-white text-center mb-16 max-w-3xl mx-auto leading-tight"
        >
          {isSpanish ? '¿Por qué no compran más personas?' : 'Why are more people not buying?'}
          <br />
          <span className="text-zinc-500 text-2xl md:text-4xl">
            {t.problem.h2Span}
          </span>
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {t.problem.points.map((point, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="p-8 bg-zinc-950 border border-white/5 rounded-2xl hover:border-orange-500/30 transition-colors"
            >
              <SafeIcon icon={icons[index]} className="text-3xl text-orange-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">{point.title}</h3>
              <p className="text-zinc-400 leading-relaxed">{point.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-24"
        >
          <p className="text-xl md:text-2xl text-zinc-300 font-medium italic">
            "{t.problem.result} <span className="text-orange-500">{t.problem.resultSpan}</span>"
          </p>
        </motion.div>

        <div className="text-center mt-20 mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
            {t.caseStudies.header}
          </h2>
          <p className="mt-4 text-zinc-500 text-sm font-medium">
            <time dateTime="2026-01">Updated: January 2026</time>
          </p>
        </div>

        <div className="space-y-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {featuredStudies.map((study, index) => (
              <motion.div
                key={study.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-zinc-950 border border-white/5 rounded-3xl overflow-hidden hover:border-orange-500/30 transition-all duration-500"
              >
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={study.image}
                    alt={`${study.clientName} ${isSpanish ? 'caso de éxito' : 'case study'}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent opacity-60" />
                  <div className="absolute bottom-6 left-6">
                    <span className="px-3 py-1 bg-orange-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-full">
                      {study.category}
                    </span>
                  </div>
                </div>

                <div className="p-8">
                  <h2 className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-2">
                    {study.clientName}
                  </h2>
                  <h3 className="text-xl font-bold text-white mb-6 group-hover:text-orange-400 transition-colors">
                    {study[lang].title}
                  </h3>
                  <Link
                    to={`/case-studies/${study.slug}`}
                    className="inline-flex items-center gap-2 text-white font-bold text-xs uppercase tracking-widest hover:text-orange-500 transition-colors group"
                  >
                    {t.caseStudies.viewFull}
                    <span className="text-orange-500">→</span>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 text-zinc-400 font-bold hover:text-orange-500 transition-colors group text-sm uppercase tracking-widest"
            >
              {t.problem.viewAll}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Problem;